import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Upload, Trash2, Loader2, Film, ShieldCheck, Plus, Save, X,
  Tags, Inbox, ClipboardList, Check, RotateCcw, ChevronDown, ChevronUp,
  Users, Crown, Ban, RefreshCw, Home, FileEdit,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { value: "standard", label: "Standard" },
  { value: "vip", label: "VIP" },
];

const emptyPkg = {
  id: null, name: "", tagline: "", price: "", features: "",
  tier: "standard", sort_order: 99, stripe_link: "",
};

export default function ClipsTab() {
  const [clips, setClips] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/clips`);
      setClips(r.data);
    } catch {
      toast.error("Failed to load clips");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) {
      toast.error("Title and video file are required");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("file", file);
      await axios.post(`${API}/admin/clips`, fd, {
        headers: { "Content-Type": "multipart/form-data" },});
      toast.success("Clip uploaded");
      setTitle("");
      setFile(null);
      const input = document.getElementById("clip-file-input");
      if (input) input.value = "";
      await load();
    } catch (err) {
      {
        toast.error("Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this clip?")) return;
    try {
      await axios.delete(`${API}/admin/clips/${id}`);
      toast.success("Clip deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <form
        onSubmit={upload}
        className="rv-card rounded-xl p-7 grid md:grid-cols-[1fr_auto] gap-4 items-end"
        data-testid="upload-form"
      >
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-white/60">Title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="3K Ranked Insane Beam"
            required
            className="bg-black/40 border-white/10 text-white"
            data-testid="clip-title-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-white/60">Video file (mp4 / mov / webm)</Label>
          <input
            id="clip-file-input"
            type="file"
            accept="video/mp4,video/quicktime,video/webm"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            required
            className="block text-sm text-white/80 file:mr-4 file:py-2.5 file:px-4 file:rounded-md file:border-0 file:font-semibold file:bg-[var(--rv-red)] file:text-white hover:file:bg-[#f04857] cursor-pointer"
            data-testid="clip-file-input"
          />
        </div>
        <button type="submit" disabled={uploading} className="btn-primary justify-center md:col-span-2" data-testid="upload-clip-btn">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <><Upload size={16} /> Upload clip</>}
        </button>
      </form>

      <div className="mt-12">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold uppercase text-xl">Uploaded clips</h2>
          <div className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-2">
            <Film size={14} /> {clips.length} total
          </div>
        </div>

        {clips.length === 0 ? (
          <div className="rv-card rounded-xl p-12 text-center mt-6 text-white/50 text-sm">No clips uploaded yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 mt-6" data-testid="admin-clip-list">
            {clips.map((c) => (
              <div key={c.id} className="rv-card rounded-xl p-4 flex gap-4" data-testid={`admin-clip-${c.id}`}>
                <video src={c.video_url} muted className="w-32 h-20 object-cover rounded bg-black flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display uppercase tracking-tight truncate">{c.title}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
                    {new Date(c.created_at).toLocaleString()}
                  </div>
                  <button
                    onClick={() => remove(c.id)}
                    className="mt-3 text-xs uppercase tracking-widest text-white/60 hover:text-[var(--rv-red)] inline-flex items-center gap-1.5"
                    data-testid={`delete-clip-${c.id}`}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
